import {
  AssetSection,
  SeedMetric,
  VulnerabilityMetrics,
} from '@/sections/monitoring/types';
import { MonitoringCounts } from '@/types';

import { MONITORING_NAME_MAP } from './constants';

/**
 * Statistic entity from API response
 */
interface StatisticEntity {
  name?: string;
  value: string;
  data?: {
    count?: number;
    Count?: number;
  };
}

/**
 * Transform vulnerability statistics from API to component format
 */
export const transformVulnerabilityStats = (
  stats?: StatisticEntity[]
): VulnerabilityMetrics => {
  if (!stats) {
    return {
      critical: { open: 0, pending: 0 },
      high: { open: 0, pending: 0 },
      medium: { open: 0, pending: 0 },
      low: { open: 0, pending: 0 },
      info: { open: 0, pending: 0 },
      exposure: { open: 0, pending: 0 },
    };
  }

  const map = new Map<string, number>();
  stats.forEach(stat => {
    map.set(stat.value, stat.data?.count || 0);
  });

  return {
    critical: {
      open: map.get('OC') || 0,
      pending: map.get('TC') || 0,
    },
    high: {
      open: map.get('OH') || 0,
      pending: map.get('TH') || 0,
    },
    medium: {
      open: map.get('OM') || 0,
      pending: map.get('TM') || 0,
    },
    low: {
      open: map.get('OL') || 0,
      pending: map.get('TL') || 0,
    },
    info: {
      open: map.get('OI') || 0,
      pending: map.get('TI') || 0,
    },
    exposure: {
      open: map.get('OE') || 0,
      pending: map.get('TE') || 0,
    },
  };
};

/**
 * Calculate the count for a single cloud provider based on the attack surface configuration.
 *
 * - `cloud=true`: counts both external AND internal assets (full cloud coverage)
 * - `cloud=false, external=true`: counts only external-facing cloud assets
 * - otherwise: returns 0
 */
export function calculateCloudProviderCount(
  provider: { external: number; internal: number; resources: number },
  attackSurface: Record<string, boolean>
): number {
  if (attackSurface.cloud) return provider.external + provider.internal;
  if (attackSurface.external) return provider.external;
  return 0;
}

/**
 * Calculate total for cloud section
 */
const calculateCloudTotal = (cloud: MonitoringCounts['cloud']): number => {
  return (
    cloud.amazon.external +
    cloud.azure.external +
    cloud.gcp.external +
    cloud.digitalocean.external
  );
};

/**
 * Calculate total for external network section
 */
const calculateExternalNetworkTotal = (
  externalNetwork: MonitoringCounts['externalNetwork']
): number => {
  return (
    externalNetwork.ipv4 +
    externalNetwork.ipv6 +
    externalNetwork.domain +
    externalNetwork.subdomain
  );
};

/**
 * Calculate total for internal network section
 */
const calculateInternalNetworkTotal = (
  internalNetwork: MonitoringCounts['internalNetwork']
): number => {
  return (
    internalNetwork.ipv4 + internalNetwork.ADDomain + internalNetwork.ADComputer
  );
};

/**
 * Calculate total for applications section
 */
const calculateApplicationsTotal = (
  applications: MonitoringCounts['applications']
): number => {
  return (
    applications.webapplication + applications.webseed + applications.webllm
  );
};

/**
 * Calculate total for code section
 */
const calculateCodeTotal = (code: MonitoringCounts['code']): number => {
  return code.github + code.gitlab + code.bitbucket + code.azuredevops;
};

/**
 * Calculate total billable assets based on selected attack surface.
 *
 * Cloud vs External precedence:
 * - `cloud=true`: counts both internal AND external cloud assets (full cloud coverage)
 * - `cloud=false, external=true`: counts only external-facing cloud assets
 *   (external network includes cloud-hosted externals)
 * The `else if` is intentional — when cloud is enabled, its external assets
 * are already included, so we skip the external-only cloud tally.
 */
export function calculateAssetTotal(
  monitoringCounts: MonitoringCounts,
  attackSurface: Record<string, boolean>
): number {
  let total = 0;

  if (attackSurface.cloud || attackSurface.external) {
    const cloud = monitoringCounts.cloud;
    total += Object.values(cloud).reduce((sum, provider) => {
      return sum + calculateCloudProviderCount(provider, attackSurface);
    }, 0);
  }

  if (attackSurface.external) {
    total += calculateExternalNetworkTotal(monitoringCounts.externalNetwork);
  }

  if (attackSurface.internal) {
    total += calculateInternalNetworkTotal(monitoringCounts.internalNetwork);
  }

  if (attackSurface.code) {
    total += calculateCodeTotal(monitoringCounts.code);
  }

  if (attackSurface.users) {
    total += monitoringCounts.internalNetwork.ADUser;
  }

  return total;
}

/**
 * Transform monitoring counts to asset sections format
 */
export const transformAssetMonitoring = (
  counts?: MonitoringCounts
): AssetSection[] => {
  if (!counts) {
    return [];
  }

  return [
    {
      key: 'cloud',
      title: MONITORING_NAME_MAP['cloud'],
      total: calculateCloudTotal(counts.cloud),
      categories: [
        {
          key: 'amazon',
          label: MONITORING_NAME_MAP['amazon'],
          count: counts.cloud.amazon.external,
        },
        {
          key: 'amazonResources',
          label: MONITORING_NAME_MAP['amazonResources'],
          count: counts.cloud.amazon.resources,
        },
        {
          key: 'azure',
          label: MONITORING_NAME_MAP['azure'],
          count: counts.cloud.azure.external,
        },
        {
          key: 'azureResources',
          label: MONITORING_NAME_MAP['azureResources'],
          count: counts.cloud.azure.resources,
        },
        {
          key: 'gcp',
          label: MONITORING_NAME_MAP['gcp'],
          count: counts.cloud.gcp.external,
        },
        {
          key: 'gcpResources',
          label: MONITORING_NAME_MAP['gcpResources'],
          count: counts.cloud.gcp.resources,
        },
        {
          key: 'digitalocean',
          label: MONITORING_NAME_MAP['digitalocean'],
          count: counts.cloud.digitalocean.external,
        },
        {
          key: 'digitaloceanResources',
          label: MONITORING_NAME_MAP['digitaloceanResources'],
          count: counts.cloud.digitalocean.resources,
        },
      ],
    },
    {
      key: 'externalNetwork',
      title: MONITORING_NAME_MAP['externalNetwork'],
      total: calculateExternalNetworkTotal(counts.externalNetwork),
      categories: [
        {
          key: 'ipv4',
          label: MONITORING_NAME_MAP['ipv4'],
          count: counts.externalNetwork.ipv4,
        },
        {
          key: 'ipv6',
          label: MONITORING_NAME_MAP['ipv6'],
          count: counts.externalNetwork.ipv6,
        },
        {
          key: 'domain',
          label: MONITORING_NAME_MAP['domain'],
          count: counts.externalNetwork.domain,
        },
        {
          key: 'subdomain',
          label: MONITORING_NAME_MAP['subdomain'],
          count: counts.externalNetwork.subdomain,
        },
      ],
    },
    {
      key: 'internalNetwork',
      title: MONITORING_NAME_MAP['internalNetwork'],
      total: calculateInternalNetworkTotal(counts.internalNetwork),
      categories: [
        {
          key: 'ipv4',
          label: MONITORING_NAME_MAP['ipv4'],
          count: counts.internalNetwork.ipv4,
        },
        {
          key: 'ADDomain',
          label: MONITORING_NAME_MAP['ADDomain'],
          count: counts.internalNetwork.ADDomain,
        },
        {
          key: 'ADUser',
          label: MONITORING_NAME_MAP['ADUser'],
          count: counts.internalNetwork.ADUser,
        },
        {
          key: 'ADComputer',
          label: MONITORING_NAME_MAP['ADComputer'],
          count: counts.internalNetwork.ADComputer,
        },
      ],
    },
    {
      key: 'applications',
      title: MONITORING_NAME_MAP['applications'],
      total: calculateApplicationsTotal(counts.applications),
      categories: [
        {
          key: 'webapplication',
          label: MONITORING_NAME_MAP['webapplication'],
          count: counts.applications.webapplication,
        },
        {
          key: 'webseed',
          label: MONITORING_NAME_MAP['webseed'],
          count: counts.applications.webseed,
        },
        {
          key: 'webllm',
          label: MONITORING_NAME_MAP['webllm'],
          count: counts.applications.webllm,
        },
      ],
    },
    {
      key: 'code',
      title: MONITORING_NAME_MAP['code'],
      total: calculateCodeTotal(counts.code),
      categories: [
        {
          key: 'github',
          label: MONITORING_NAME_MAP['github'],
          count: counts.code.github,
        },
        {
          key: 'gitlab',
          label: MONITORING_NAME_MAP['gitlab'],
          count: counts.code.gitlab,
        },
        {
          key: 'bitbucket',
          label: MONITORING_NAME_MAP['bitbucket'],
          count: counts.code.bitbucket,
        },
        {
          key: 'azuredevops',
          label: MONITORING_NAME_MAP['azuredevops'],
          count: counts.code.azuredevops,
        },
      ],
    },
  ];
};

/**
 * Transform seed statistics to metrics format
 */
export const transformSeedStats = (stats?: StatisticEntity[]): SeedMetric[] => {
  if (!stats) {
    return [];
  }

  const map = new Map<string, number>();
  stats.forEach(stat => {
    map.set(`${stat.name}#${stat.value}`, stat.data?.count || 0);
  });

  return [
    {
      type: 'domain',
      label: 'Domains',
      approved: map.get('domain#A') || 0,
      pending: map.get('domain#P') || 0,
    },
    {
      type: 'tld',
      label: 'TLD',
      approved: map.get('tld#A') || 0,
      pending: map.get('tld#P') || 0,
    },
    {
      type: 'ipv4',
      label: 'IPv4',
      approved: map.get('ipv4#A') || 0,
      pending: map.get('ipv4#P') || 0,
    },
    {
      type: 'ipv6',
      label: 'IPv6',
      approved: map.get('ipv6#A') || 0,
      pending: map.get('ipv6#P') || 0,
    },
    {
      type: 'cidr',
      label: 'CIDR',
      approved: map.get('cidr#A') || 0,
      pending: map.get('cidr#P') || 0,
    },
  ];
};
